// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * image.ts — pure, dependency-free helpers for Pixpress.
 *
 * Everything here is synchronous and side-effect-free so it can be unit-tested
 * without a DOM or the wasm encoders: the output-format registry, filename
 * derivation, resize maths, byte formatting and savings arithmetic.
 */

export type OutputFormat = 'webp' | 'jpeg' | 'avif' | 'png';

export interface FormatSpec {
  /** Output MIME type. */
  mime: string;
  /** File extension (no dot). */
  ext: string;
  /** Whether the format honours a 0–1 quality parameter. */
  lossy: boolean;
  /** Short label shown in the UI. */
  label: string;
  /** One-line hint under the format picker. */
  hint: string;
}

export const FORMATS: Record<OutputFormat, FormatSpec> = {
  webp: {
    mime: 'image/webp',
    ext: 'webp',
    lossy: true,
    label: 'WebP',
    hint: 'Best all-round size for the web',
  },
  jpeg: {
    mime: 'image/jpeg',
    ext: 'jpg',
    lossy: true,
    label: 'JPEG',
    hint: 'Universal — opens everywhere',
  },
  avif: {
    mime: 'image/avif',
    ext: 'avif',
    lossy: true,
    label: 'AVIF',
    hint: 'Smallest files, slower to encode',
  },
  png: {
    mime: 'image/png',
    ext: 'png',
    lossy: false,
    label: 'PNG',
    hint: 'Lossless — optimised, not recompressed',
  },
};

export const DEFAULT_FORMAT: OutputFormat = 'webp';
export const DEFAULT_QUALITY = 0.75;
/** 0 = no resize. Otherwise the longest edge is clamped to this many pixels. */
export const DEFAULT_MAX_DIM = 0;

export function isOutputFormat(v: string): v is OutputFormat {
  return v === 'webp' || v === 'jpeg' || v === 'avif' || v === 'png';
}

/** Extensions we swap out when deriving an output filename. */
const STRIPPABLE_EXT = /\.(jpe?g|png|webp|avif|gif|bmp|heic|heif|tiff?)$/i;

/**
 * Derive an output filename: replace a known image extension with the target
 * extension, or append it when there's nothing to strip. Marks the format in
 * the stem so re-compressing at different settings doesn't collide.
 *
 *   toOutputName('IMG_0001.JPG', 'webp') -> 'IMG_0001.webp'
 *   toOutputName('my.photo.png', 'jpg')  -> 'my.photo.jpg'
 *   toOutputName('scan',         'avif') -> 'scan.avif'
 */
export function toOutputName(inputName: string, ext: string): string {
  const trimmed = (inputName || 'image').trim() || 'image';
  const base = STRIPPABLE_EXT.test(trimmed) ? trimmed.replace(STRIPPABLE_EXT, '') : trimmed;
  return `${base}.${ext}`;
}

export interface ResizeResult {
  width: number;
  height: number;
  scaled: boolean;
}

/**
 * Given source dimensions and a longest-edge cap (0 = disabled), return the
 * target dimensions. Never upscales; preserves aspect ratio; clamps to >= 1px.
 */
export function computeResize(width: number, height: number, maxDim: number): ResizeResult {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) {
    return { width: Math.max(1, Math.round(width) || 1), height: Math.max(1, Math.round(height) || 1), scaled: false };
  }
  const longest = Math.max(width, height);
  if (!maxDim || maxDim <= 0 || longest <= maxDim) {
    return { width: Math.round(width), height: Math.round(height), scaled: false };
  }
  const scale = maxDim / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scaled: true,
  };
}

/** Human-readable byte size. */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${Math.round(n)} B`;
  const units = ['KB', 'MB', 'GB'];
  let val = n / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${val < 10 ? val.toFixed(1) : Math.round(val)} ${units[i]}`;
}

/** Signed percentage change from `before` to `after` (negative = smaller). */
export function sizeDeltaPct(before: number, after: number): number {
  if (before <= 0) return 0;
  return Math.round(((after - before) / before) * 100);
}

/** Convert a 0–1 UI quality to the 1–100 integer the wasm encoders expect. */
export function qualityToPercent(q: number): number {
  const pct = Math.round(q * 100);
  return Math.min(100, Math.max(1, pct));
}
